// @flow
import React, { PureComponent } from 'react';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import i18n from 'app/common/lib/i18n';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { inOpenModalExperience } from 'app/common/react/components/growth/unauth/open/hybrid/hybridExperimentUtils';
import { isInLoginSignupModalCooldown } from 'app/common/react/components/growth/unauth/open/hybrid/closeupBannerUtils';
import { Box, Text } from 'gestalt';

type Props = {
  bannerType?: string,
  hideLoginSignupButtons?: boolean,
  modalType?: string,
  modalBehavior?: string,
  onLoginClick: (e: SyntheticEvent<>) => void,
  onSignupClick: (e: SyntheticEvent<>) => void,
  showSignupLoginModal: (?string, boolean) => void,
  viewsLeft?: number,
};

export const MODAL_TYPE = {
  LOGIN: 'login',
  SIGNUP: 'signup',
  SIGNUP_OR_PERSONAL_LOGIN: 'signupOrPersonalizedLogin',
};

export const MODAL_BEHAVIOR = {
  NO_MODAL: 'noModal',
  BLOCKING: 'blocking',
  DISMISSIBLE: 'dismissible',
};

export const BANNER_TYPE = {
  NO_BANNER: 'noBanner',
  BANNER_WITH_COUNTER: 'bannerWithCounter',
  BANNER_WITHOUT_COUNTER: 'bannerWithoutCounter',
};

const styles = {
  countdownNumber: {
    color: '#fff',
    fontSize: '36px',
    fontWeight: 'bold',
    lineHeight: '36px',
  },
  lockingBannerStyle: {
    cursor: 'default',
    zIndex: 1,
    position: 'fixed',
  },
  loginButtonStyle: {
    height: '36px',
    minWidth: '128px',
    marginTop: '0',
  },
  signupButtonStyle: {
    color: '#0084ff',
    height: '36px',
    marginRight: '5px',
    marginTop: '0',
    minWidth: '128px',
  },
  verticalDivider: {
    background: 'rgb(255, 255, 255, 0.75)',
    height: '72px',
    width: '1px',
  },
};

export default class HybridCloseupBanner extends PureComponent<Props> {
  onBannerClick = (event: SyntheticEvent<>) => {
    // do not let this event propagate
    event.stopPropagation();
  };

  getTitleText(bannerType: ?string, viewsLeft?: number) {
    if (
      bannerType === BANNER_TYPE.BANNER_WITH_COUNTER &&
      viewsLeft !== undefined &&
      viewsLeft > 0
    ) {
      return i18n._(
        'Only a few views left—sign up or log in now',
        'Pinterest value prop for education banner',
      );
    } else {
      return i18n._(
        'Discover more ideas with a Pinterest account',
        'Pinterest value prop for education banner',
      );
    }
  }

  getSubtitleText(bannerType: ?string, viewsLeft?: number) {
    if (
      bannerType === BANNER_TYPE.BANNER_WITH_COUNTER &&
      viewsLeft !== undefined &&
      viewsLeft > 0
    ) {
      return i18n._(
        'Explore more ideas with a free Pinterest account',
        'Subtitle text for education banner',
      );
    } else {
      return i18n._(
        'Sign up or log in to continue exploring',
        'Subtitle text for education banner',
      );
    }
  }

  showModal(modalBehavior: ?string, modalType: ?string) {
    if (modalBehavior && modalBehavior !== MODAL_BEHAVIOR.NO_MODAL) {
      const dismissible = modalBehavior === MODAL_BEHAVIOR.DISMISSIBLE;
      this.props.showSignupLoginModal(modalType, dismissible);
    }
  }

  renderLoginSignupButtons() {
    const { onLoginClick, onSignupClick } = this.props;
    trackRegisterAction('unauth.close_up_view.hybrid_education_banner.signup_login_button.shown');
    return (
      <Box display="flex" justifyContent="between" marginRight={3} padding={2}>
        <Button className="white" onClick={onSignupClick} styleOverrides={styles.signupButtonStyle}>
          {i18n._('Sign up', 'word in the sign up button')}
        </Button>
        <Button
          className="blueTransparent"
          onClick={onLoginClick}
          styleOverrides={styles.loginButtonStyle}
        >
          {i18n._('Log in', 'word in the log in button')}
        </Button>
      </Box>
    );
  }

  renderViewCountdown(numViewsLeft: number) {
    return (
      <Box display="flex" top left minWidth={65}>
        <Box
          alignItems="center"
          display="flex"
          direction="column"
          justifyContent="center"
          marginLeft={1}
          marginRight={4}
        >
          <span style={styles.countdownNumber}>{numViewsLeft}</span>
          <Text color="white" size="xs">
            {i18n.ngettext('view left', 'views left', numViewsLeft, 'number of closeup views left')}
          </Text>
        </Box>
        <div style={styles.verticalDivider} />
      </Box>
    );
  }

  render() {
    const { modalBehavior, modalType, bannerType, hideLoginSignupButtons, viewsLeft } = this.props;

    const titleText = this.getTitleText(bannerType, viewsLeft);
    const subtitleText = this.getSubtitleText(bannerType, viewsLeft);

    if (
      (modalBehavior === MODAL_BEHAVIOR.BLOCKING || !isInLoginSignupModalCooldown()) &&
      !inOpenModalExperience()
    ) {
      trackRegisterAction('unauth.close_up_view.hybrid_education_banner.signup_login_modal.shown');
      this.showModal(modalBehavior, modalType);
    }

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div onClick={this.onBannerClick} onKeyPress={this.onBannerClick}>
        <Box
          alignItems="center"
          color="blue"
          dangerouslySetInlineStyle={{ __style: styles.lockingBannerStyle }}
          display="flex"
          height="72px"
          padding={3}
          width="100%"
        >
          {bannerType &&
          bannerType === BANNER_TYPE.BANNER_WITH_COUNTER &&
          viewsLeft !== undefined &&
          !inOpenModalExperience()
            ? this.renderViewCountdown(viewsLeft)
            : null}
          <Box
            display="flex"
            alignItems="center"
            flex="grow"
            justifyContent="between"
            marginLeft={6}
          >
            <Box display="flex" direction="column">
              <Text bold color="white" overflow="breakWord" size="lg">
                {titleText}
              </Text>
              <Text color="white" overflow="breakWord" size="md">
                {subtitleText}
              </Text>
            </Box>
            {!hideLoginSignupButtons || inOpenModalExperience()
              ? this.renderLoginSignupButtons()
              : null}
          </Box>
        </Box>
      </div>
    );
  }
}
